<template>
  <div class="px-4 pb-4 sm:px-6 sm:pb-6">
    <h1 class="my-2">{{ t("headings.user") }}</h1>
    <breadcrumb class="mt-3 mb-4" />
    <div class="card p-4">
      <div
        class="grid grid-cols-1 gap-x-12 gap-y-4 p-4 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-4"
      >
        <div class="flex flex-col items-center md:col-span-2 lg:col-span-1">
          <div
            v-if="waiting"
            class="aspect-square flex w-80 max-w-full animate-pulse items-center justify-center rounded-full bg-slate-300"
          />
          <img
            v-else-if="user.avatar"
            class="w-80 max-w-full rounded-full object-cover"
            :src="getAvatar(user)"
          />
          <div
            v-else
            class="aspect-square flex w-80 max-w-full items-center justify-center rounded-full bg-slate-300"
          >
            <UserIcon class="h-3/4 w-3/4 text-white" />
          </div>
          <div class="mt-4 flex justify-center">
            <form-filepicker
              accept="image/jpeg, image/png"
              :disabled="waiting"
              id="user-image-upload"
              label="change_avatar"
              :multiple="false"
              :textVariant="company.primaryText || 'light'"
              :variant="company.primary || 'indigo'"
              v-model="images"
            />
          </div>
        </div>
        <div class="flex flex-col gap-y-4 sm:py-4 xl:mt-14">
          <div>
            <label class="block">{{ t("labels.first_name") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else>{{ user.firstName }}</span>
          </div>
          <div>
            <label class="block">{{ t("labels.last_name") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else>{{ user.lastName }}</span>
          </div>
          <div>
            <label class="block">{{ t("labels.role") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else>{{
              estate.id && user.estates
                ? t(
                    `roles.${getRoleName(
                      user.estates[estate.id].roleId,
                      roles
                    )}`
                  )
                : user.roleId
                ? t(`roles.${getRoleName(user.roleId, roles)}`)
                : null
            }}</span>
          </div>
          <div>
            <label class="block">{{ t("labels.language") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else>{{ getLanguage(user.language) }}</span>
          </div>
        </div>
        <div class="flex flex-col gap-y-4 sm:py-4 xl:col-span-2 xl:mt-14">
          <div>
            <label class="block">{{ t("labels.address") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else-if="user.address"
              >{{ user.address }}, {{ user.postalCode }} {{ user.city }}</span
            >
          </div>
          <div>
            <label class="block">{{ t("labels.email") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else>{{ user.email }}</span>
          </div>
          <div>
            <label class="block">{{ t("labels.phone") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else>{{ user.phone }}</span>
          </div>
          <div v-if="estate.id && user.estates && user.estates[estate.id]">
            <label class="block">{{ t("labels.apartments") }}</label>
            <span v-if="waiting">
              <skeleton class="h-5 w-full" />
            </span>
            <span v-else>{{
              user.estates[estate.id].apartments
                .map((apartment) => apartment.identifier)
                .join(", ")
            }}</span>
          </div>
        </div>
      </div>
      <hr class="my-4 border-slate-300" />
      <div class="flex justify-end">
        <form-button
          :command="
            () =>
              route.params.estateId
                ? router.push(`/estates/${estate.id}/persons/${user.id}/edit`)
                : router.push(`/personnel/${user.id}/edit`)
          "
          :disabled="waiting"
          id="profile-edit-button"
          label="edit"
          :textVariant="company.primaryText || 'light'"
          type="button"
          :variant="company.primary || 'indigo'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { UserIcon } from "@heroicons/vue/24/outline";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import Breadcrumb from "../components/breadcrumb/Breadcrumb.vue";
import FormButton from "../components/form/FormButton.vue";
import FormFilepicker from "../components/form/FormFilepicker.vue";
import Skeleton from "../components/loading/Skeleton.vue";
import { getAvatar } from "../utils/imageUtils";
import { getLanguage } from "../utils/languageUtils";
import { getRoleName } from "../utils/roleUtils";
export default {
  components: { Breadcrumb, FormButton, FormFilepicker, Skeleton, UserIcon },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const { t } = useI18n();

    const company = computed(() => store.state.company.company);
    const estate = computed(() => store.state.estate.estate);
    const roles = computed(() => store.state.role.roles);
    const user = computed(() => store.state.user.user);

    const waiting = computed(() => {
      if (store.state.company.waiting) return true;
      if (store.state.estate.waiting) return true;
      if (store.state.role.waiting) return true;
      if (store.state.user.waiting) return true;
      return false;
    });

    onMounted(() => {
      if (!user.value.id || user.value.id != route.params.userId) {
        store.dispatch("user/getUser", route.params.userId);
      }
      if (
        route.params.estateId &&
        (!estate.value.id || estate.value.id != route.params.estateId)
      ) {
        store.dispatch("estate/getEstate", route.params.estateId);
      }
      if (!roles.value.length) {
        store.dispatch("role/getRoles");
      }
    });

    const images = ref([]);

    watch(
      () => images.value,
      (images) => {
        if (images?.length) {
          store.dispatch("user/updateAvatar", {
            id: user.value.id,
            file: images[0],
          });
        }
      },
      {
        deep: true,
      }
    );

    return {
      company,
      estate,
      getAvatar,
      getLanguage,
      getRoleName,
      images,
      roles,
      route,
      router,
      t,
      user,
      waiting,
    };
  },
};
</script>
